import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { Col, Form, Row } from 'antd';
import { orderBy, uniq } from 'es-toolkit';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as SaveIcon } from '../../../assets/icons/save.svg';
import { GET_QUOTE_FORM_IDS } from '../../../contants/ShipmentEnums';
import { APP_ROUTES } from '../../../contants/routes';
import { setAppLoader, setPageBlocker } from '../../../redux/common/CommonActions';
import { selectIsPageBlockerActive } from '../../../redux/common/CommonReducer';
import useShipmentRoutingSettings from '../../../redux/hooks/useShipmentRoutingSettings';
import {
  createShipmentQuoteInProgress,
  getShipmentDefaultAccessorialsInProgress,
  resetCreateShipmentQuote,
  resetShipmentRateShopList,
} from '../../../redux/shipment/ShipmentActions';
import { getShipmentRateShopListEffect } from '../../../redux/shipment/ShipmentEffects';
import { routeFileSelector } from '../../../utilz/i18n';
import {
  convertRateEngineApiResponseToShipmentLocal,
  convertRateShopResponseToAPI,
  parseCreateQuoteDataFromFormData,
  parseQuoteDataToRateEngine,
} from '../../../utilz/shipmentDataManupulation';
import Button from '../../components/Buttons/Button';
import IconButton from '../../components/Buttons/IconButton';
import SvgConverter from '../../components/Common/SvgConverter';
import PrimaryLabel from '../../components/Labels/PrimaryLabel';
import SecondaryLabel from '../../components/Labels/SecondaryLabel';
import CommonModal from '../../components/Modals/CommonModal';
import Paper from '../../components/Paper/Paper';
import TMSCreateGetQuoteTabs from './components/CreateGetQuote/TMSCreateGetQuoteTabs';
import SpotBidDetails from './components/Tabs/SpotBid/SpotBidDetails';
import { useGetShipmentBranchList } from './hooks';

// todo GetQuoteScreen for TMS page
const TMSGetQuoteScreen = () => {
  const { selectedCompany } = useSelector((state) => state.auth);
  const { createShipmentQuote, defaultAccessorials } = useSelector((state) => state.shipment);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const havingAccessForMenu = useShipmentRoutingSettings('isMenuVisible');
  const [activeKey, setActiveKey] = useState(['1', '2', '3', '4', '5', '6', '7', '8']);
  const [rateShopList, setRateShopList] = useState([]);
  const [selectedRateIndex, setSelectedRateIndex] = useState(false);
  const { data: shipmentBranches = [] } = useGetShipmentBranchList({
    Customerkey: selectedCompany.companyId,
  });
  const [spotBidModal, setSpotBidModal] = useState(false);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  useEffect(() => {
    dispatch(resetShipmentRateShopList());
  }, []);

  useEffect(() => {
    if (!defaultAccessorials?.success && selectedCompany?.companyId) {
      const payload = {
        shipmentKey: 0,
        customerKey: selectedCompany?.companyId,
      };
      dispatch(getShipmentDefaultAccessorialsInProgress(payload));
    }
  }, [defaultAccessorials?.success, selectedCompany]);

  const handleSuccessCallBack = () => {
    dispatch(setPageBlocker(false));
    setSpotBidModal(false);
    navigate(APP_ROUTES.SHIPMENT);
  };

  const handleSubmit = async (triggeredForm, { forms }) => {
    try {
      if (['saveQuoteForm', 'rateShopForm', 'spotBidForm'].includes(triggeredForm)) {
        const formList = [
          validateForm(forms.quoteDetailsForm, 'quoteDetailsForm'),
          validateForm(forms.stopsForm, 'stopsForm'),
          validateForm(forms.productForm, 'productForm'),
          validateForm(forms.transportationProviderSearchForm, 'transportationProviderSearchForm'),
          validateForm(forms.notesForm, 'notesForm'),
          validateForm(forms.documentForm, 'documentForm'),
        ];

        if (havingAccessForMenu) {
          formList.push(validateForm(forms.additionalServicesForm, 'additionalServicesForm'));
        }

        let formResponse = await Promise.all(formList);
        formResponse = formResponse.reduce((a, b) => {
          // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
          return { ...a, ...b };
        }, {});

        if (!formResponse?.stopsForm?.stops?.find((item) => item.stopTypeID === '1')) {
          showNotification({
            type: NOTIFICATION_TYPES.ERROR,
            message: t('translation:getQuote.triggerNotification'),
          });
          setActiveKey([GET_QUOTE_FORM_IDS.stopsForm]);
        } else if (!formResponse?.stopsForm?.stops.find((item) => item.stopTypeID === '2')) {
          showNotification({
            type: NOTIFICATION_TYPES.ERROR,
            message: t('translation:getQuote.destionationTriggerNotification'),
          });
          setActiveKey([GET_QUOTE_FORM_IDS.stopsForm]);
        } else if (!formResponse?.productForm?.items || formResponse?.productForm?.items?.length === 0) {
          showNotification({
            type: NOTIFICATION_TYPES.ERROR,
            message: t('translation:getQuote.productTriggerNotification'),
          });
          setActiveKey([GET_QUOTE_FORM_IDS.productForm]);
        } else {
          const data = parseCreateQuoteDataFromFormData({
            ...formResponse.quoteDetailsForm,
            ...formResponse.notesForm,
            ...formResponse.documentForm,
            ...formResponse.transportationProviderSearchForm,
            stops: formResponse?.stopsForm?.stops,
            products: formResponse?.productForm?.items,
            accessorials: formResponse?.additionalServicesForm?.accessorials?.filter((item) => item.isChecked),
            customerKey: selectedCompany?.companyId,
          });
          data.selectedRate = {};
          if (triggeredForm === 'saveQuoteForm' || triggeredForm === 'spotBidForm') {
            if (selectedRateIndex >= 0 && typeof selectedRateIndex === 'number') {
              data.selectedRate = convertRateShopResponseToAPI(
                [rateShopList[selectedRateIndex]],
                selectedCompany?.companyId,
                rateShopList?.[selectedRateIndex]?.carrierKey,
              )?.[0];
            } else {
              data.selectedRate = {};
            }

            if (rateShopList?.length > 0) {
              data.listRateShopDataAPI = convertRateShopResponseToAPI(
                rateShopList,
                selectedCompany?.companyId,
                rateShopList?.[selectedRateIndex]?.carrierKey,
              );
            } else {
              data.listRateShopDataAP = [];
            }
            const handleSuccessCallBackLocal = () => {
              setSpotBidModal(true);
            };
            dispatch(
              createShipmentQuoteInProgress({
                formData: data,
                handleSuccessCallBack:
                  triggeredForm === 'spotBidForm' ? handleSuccessCallBackLocal : handleSuccessCallBack,
              }),
            );
          } else if (triggeredForm === 'rateShopForm') {
            if (!havingAccessForMenu) {
              data.accessorials = defaultAccessorials?.data;
            } else {
              data.accessorials = data?.accessorialGridModel;
            }
            const branchDetails = shipmentBranches?.find((item) => item.branchKey === Number(data.branch));
            data.currencyCode = branchDetails?.currencyCode ? branchDetails?.currencyCode : '**';
            const formData = parseQuoteDataToRateEngine(data);
            getRateShopData(formData);
          }
        }
      }
    } catch (error) {
      console.log(error);
      if (['stopsForm', 'productForm'].includes(error?.formName)) {
        let errorFieldsIndex = error?.errorFields?.map(({ name }) => name[1]);
        errorFieldsIndex = uniq(errorFieldsIndex);
        const listName = error?.formName === 'stopsForm' ? 'stops' : 'items';
        const stops = forms?.[error?.formName].getFieldValue(listName);
        errorFieldsIndex.forEach((i) => {
          stops[i].editMode = true;
        });
        forms?.[error?.formName].setFieldValue('stops', stops);
      }
      setActiveKey([GET_QUOTE_FORM_IDS[error?.formName]]);
    }
  };

  const getRateShopData = async (formData) => {
    try {
      dispatch(setAppLoader(true));
      const { data } = await getShipmentRateShopListEffect(formData);
      setRateShopList(orderBy(convertRateEngineApiResponseToShipmentLocal(data), ['convertedCost'], ['asc']));
      dispatch(setAppLoader(false));
      setActiveKey([havingAccessForMenu ? '6' : '5']);
    } catch {
      dispatch(setAppLoader(false));
      setRateShopList(false);
      setActiveKey([havingAccessForMenu ? '6' : '5']);
    }
  };

  const validateForm = (formInstance, formName) => {
    // eslint-disable-next-line no-async-promise-executor
    // biome-ignore lint/suspicious/noAsyncPromiseExecutor: <explanation>
    return new Promise(async (resolve, reject) => {
      try {
        const validationStatus = await formInstance.validateFields();
        resolve({ [formName]: validationStatus });
      } catch (error) {
        reject({ formName, ...error });
      }
    });
  };

  useEffect(() => {
    return () => {
      dispatch(resetCreateShipmentQuote());
    };
  }, []);
  const isPageBlockerActive = useSelector(selectIsPageBlockerActive);

  return (
    <>
      <Form.Provider
        onFormFinish={handleSubmit}
        onFormChange={() => {
          if (!isPageBlockerActive) {
            dispatch(setPageBlocker(true));
          }
        }}
      >
        <Row className="flex-direction-column flex-nowrap  h-100 ">
          <Col className="mb-15">
            <Paper className="p-15 ">
              <Row align="middle" justify="space-between" gutter={30}>
                <Col>
                  <PrimaryLabel className="text-black">{t('translation:getQuote.titles.getQuote')}</PrimaryLabel>
                </Col>
                <Col>
                  <Row gutter={30} align="middle">
                    {createShipmentQuote?.success && (
                      <Col>
                        <SecondaryLabel className="f-18 fw-600 text-success">
                          {t('translation:default.fields.shipmentNo')}:{createShipmentQuote?.data?.shipmentNumber}
                        </SecondaryLabel>
                      </Col>
                    )}

                    <Col>
                      <Form name="saveQuoteForm">
                        <IconButton buttonType="submit">
                          <Row gutter={10} align="middle">
                            <Col className="lh-0">
                              <SvgConverter
                                Icon={SaveIcon}
                                width={22}
                                height={22}
                                color={createShipmentQuote?.success ? 'disabled-text' : 'black'}
                              />
                            </Col>
                            <Col className="lh-0">
                              <SecondaryLabel
                                className={`f-14 f-400 ${createShipmentQuote?.success ? 'disabled-text' : 'black'}`}
                              >
                                {t('translation:default.buttons.save')}
                              </SecondaryLabel>
                            </Col>
                          </Row>
                        </IconButton>
                      </Form>
                    </Col>
                    <Col>
                      <Form name="rateShopForm">
                        <Button buttonType="submit">{t('translation:default.buttons.rateShop')}</Button>
                      </Form>
                    </Col>
                    <Col>
                      <Form name="spotBidForm">
                        <Button type="primary" variant="primary" buttonType="submit">
                          {t('translation:default.fields.spotBid')}
                        </Button>
                      </Form>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Paper>
          </Col>
          <Col className="flex-1 custom-scroll-bar">
            <TMSCreateGetQuoteTabs
              activeKey={activeKey}
              setActiveKey={setActiveKey}
              rateShopList={rateShopList}
              selectedRateIndex={selectedRateIndex}
              setSelectedRateIndex={setSelectedRateIndex}
            />
          </Col>
        </Row>
      </Form.Provider>
      <CommonModal
        title={<span className="ml-10">{t('translation:default.spotBid')}</span>}
        open={spotBidModal}
        className="w-80"
        contentClassName="p-30"
        customFooter={<></>}
        onClose={() => setSpotBidModal(false)}
      >
        <div className="h-100 custom-scroll-bar">
          <SpotBidDetails handleSpotBidSuccess={handleSuccessCallBack} submitSpotBidButtonContainerClassName="pb-15" />
        </div>
      </CommonModal>
    </>
  );
};

export default TMSGetQuoteScreen;
